import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { fmtTRY, fmtDate, todayISO } from "@/lib/format";
import { toast } from "sonner";
import {
  Plus, Trash2, Edit3, X, ShieldCheck, ShieldAlert, ShieldOff,
  ArrowLeftRight, AlertTriangle, ArchiveRestore, Ban, CircleDollarSign, Upload,
  ArrowUp, ArrowDown, ArrowUpDown,
} from "lucide-react";
import GuaranteeImportModal from "@/components/GuaranteeImportModal";

const LETTER_TYPES = {
  definitive: "Kesin",
  temporary: "Geçici",
  advance: "Avans",
  performance: "Performans",
  other: "Diğer",
};

const STATUS = {
  active: { label: "Aktif", color: "bg-emerald-50 text-emerald-800 border-emerald-300", icon: ShieldCheck },
  returned: { label: "İade Edildi", color: "bg-neutral-100 text-neutral-700 border-neutral-300", icon: ArchiveRestore },
  called: { label: "Nakde Çevrildi", color: "bg-rose-50 text-rose-800 border-rose-300", icon: ShieldAlert },
  expired: { label: "Süresi Doldu", color: "bg-amber-50 text-amber-800 border-amber-300", icon: AlertTriangle },
  cancelled: { label: "İptal", color: "bg-neutral-50 text-neutral-500 border-neutral-200 line-through", icon: Ban },
};

const COMMISSION_PERIODS = {
  quarterly: "3 Aylık",
  semiannual: "6 Aylık",
  annual: "Yıllık",
  one_time: "Tek Seferlik",
};

export default function Guarantees() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusModal, setStatusModal] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [sort, setSort] = useState({ field: "expiry_date", dir: "asc" });

  const toggleSort = (field) => {
    setSort((prev) => {
      if (prev.field !== field) {
        // Sayısal ve tarih alanlarında ilk tık yerine göre farklı sıralar açmak sezgisel:
        //   - Vade tarihi: en yakın vade önce (asc)
        //   - Tutar: en yüksek tutar önce (desc)
        const initDir = field === "amount" ? "desc" : "asc";
        return { field, dir: initDir };
      }
      return { field, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const load = useCallback(async () => {
    const [{ data: gs }, { data: co }, { data: ac }, { data: sm }] = await Promise.all([
      api.get("/guarantees"),
      api.get("/contacts"),
      api.get("/accounts"),
      api.get("/guarantees/summary"),
    ]);
    setItems(gs); setContacts(co); setAccounts(ac); setSummary(sm);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items.filter(
    (g) => (statusFilter === "all" || g.status === statusFilter)
       && (typeFilter === "all" || g.letter_type === typeFilter),
  ), [items, statusFilter, typeFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    const dir = sort.dir === "asc" ? 1 : -1;
    const trCol = new Intl.Collator("tr", { sensitivity: "base" });
    list.sort((a, b) => {
      let av, bv;
      switch (sort.field) {
        case "amount":
          av = Number(a.amount) || 0; bv = Number(b.amount) || 0;
          return (av - bv) * dir;
        case "expiry_date":
        case "issue_date":
          av = a[sort.field] || ""; bv = b[sort.field] || "";
          return trCol.compare(String(av), String(bv)) * dir;
        case "status":
        case "letter_type":
          av = a[sort.field] || ""; bv = b[sort.field] || "";
          return trCol.compare(av, bv) * dir;
        default:
          av = a[sort.field] || ""; bv = b[sort.field] || "";
          return trCol.compare(String(av), String(bv)) * dir;
      }
    });
    return list;
  }, [filtered, sort]);

  const remove = async (id) => {
    if (!window.confirm("Teminat mektubu silinsin mi? Nakde çevrildiyse ilgili gider de silinir.")) return;
    await api.delete(`/guarantees/${id}`);
    toast.success("Silindi");
    load();
  };

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="label-caps">Bankadan Alınan Garantiler</p>
          <h1 className="font-display text-4xl font-bold tracking-tight mt-2">Teminat Mektupları</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="guarantee-import-open"
            onClick={() => setImportOpen(true)}
            className="brutal-btn bg-white text-black border border-black px-4 py-2.5 text-sm flex items-center gap-2"
            title="Excel/CSV'den toplu içe aktar"
          >
            <Upload size={16} /> Toplu İçe Aktar
          </button>
          <button
            data-testid="guarantee-add-button"
            onClick={() => { setEditing(null); setOpen(true); }}
            className="brutal-btn bg-black text-white px-4 py-2.5 text-sm flex items-center gap-2"
          >
            <Plus size={16} /> Yeni Teminat Mektubu
          </button>
        </div>
      </header>

      {summary && (
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
          <StatCard label="Aktif TM Tutarı" value={summary.active_amount} icon={ShieldCheck}
                    subtitle={`${summary.active_count} adet aktif`} tid="stat-guarantee-active" />
          <StatCard label="30 Gün İçinde Dolan" value={summary.expiring_30_amount} tone="amber" icon={AlertTriangle}
                    subtitle={`${summary.expiring_30_count} mektup`} tid="stat-guarantee-expiring30" />
          <StatCard label="90 Gün İçinde Dolan" value={summary.expiring_90_amount} icon={AlertTriangle}
                    subtitle={`${summary.expiring_90_count} mektup`} tid="stat-guarantee-expiring90" />
          <StatCard label="Nakde Çevrilen" value={summary.called_amount} tone="rose" icon={ShieldAlert}
                    subtitle={`${summary.called_count} adet · ${summary.expired_count} süresi dolan`} tid="stat-guarantee-called" />
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex flex-wrap gap-1.5">
          {["all", ...Object.keys(STATUS)].map((s) => (
            <button
              key={s}
              data-testid={`guarantee-status-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`text-[11px] uppercase tracking-widest px-2.5 py-1 border ${
                statusFilter === s ? "bg-black text-white border-black" : "border-black/15 text-neutral-600 hover:border-black/40"
              }`}
            >
              {s === "all" ? "Tümü" : STATUS[s].label}
            </button>
          ))}
        </div>
        <div className="mx-2 h-4 w-px bg-black/20" />
        <div className="flex flex-wrap gap-1.5">
          {["all", ...Object.keys(LETTER_TYPES)].map((t) => (
            <button
              key={t}
              data-testid={`guarantee-type-filter-${t}`}
              onClick={() => setTypeFilter(t)}
              className={`text-[11px] uppercase tracking-widest px-2.5 py-1 border ${
                typeFilter === t ? "bg-neutral-900 text-white border-neutral-900" : "border-black/15 text-neutral-600 hover:border-black/40"
              }`}
            >
              {t === "all" ? "Tüm Türler" : LETTER_TYPES[t]}
            </button>
          ))}
        </div>
      </div>

      <div className="brutal-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="guarantee-table">
          <thead>
            <tr className="border-b-2 border-black/80">
              <SortableTh field="expiry_date" sort={sort} onClick={toggleSort} align="left">Vade</SortableTh>
              <SortableTh field="letter_number" sort={sort} onClick={toggleSort} align="left">Mektup No</SortableTh>
              <SortableTh field="bank_name" sort={sort} onClick={toggleSort} align="left">Banka / Tür</SortableTh>
              <SortableTh field="beneficiary_name" sort={sort} onClick={toggleSort} align="left">Lehtar</SortableTh>
              <SortableTh field="project" sort={sort} onClick={toggleSort} align="left">Proje</SortableTh>
              <SortableTh field="status" sort={sort} onClick={toggleSort} align="left">Durum</SortableTh>
              <SortableTh field="amount" sort={sort} onClick={toggleSort} align="right">Tutar</SortableTh>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={8} className="text-center py-10 text-neutral-500">Kayıt yok. Yeni teminat mektubu ekleyin.</td></tr>
            )}
            {sorted.map((g) => {
              const S = STATUS[g.status];
              return (
                <tr key={g.id} className="border-b border-black/5 hover:bg-neutral-50">
                  <td className="px-4 py-3 font-mono-num">
                    {fmtDate(g.expiry_date)}
                    <div className="text-[10px] text-neutral-400 mt-0.5">Düz: {fmtDate(g.issue_date)}</div>
                  </td>
                  <td className="px-4 py-3 font-mono-num">{g.letter_number}</td>
                  <td className="px-4 py-3">
                    <div>{g.bank_name}</div>
                    <div className="text-[11px] text-neutral-500 uppercase tracking-widest">{LETTER_TYPES[g.letter_type]}</div>
                  </td>
                  <td className="px-4 py-3 text-neutral-700">
                    {g.beneficiary_name}
                    {g.beneficiary_contact_id && (
                      <div className="text-[10px] text-neutral-400">{contacts.find(c => c.id === g.beneficiary_contact_id)?.name}</div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-neutral-700">{g.project || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`border px-2 py-1 text-[11px] uppercase tracking-widest inline-flex items-center gap-1 ${S.color}`}>
                      <S.icon size={11} />
                      {S.label}
                    </span>
                    {g.commission_rate ? (
                      <div className="text-[10px] text-neutral-400 mt-1">Kom: %{g.commission_rate} · {COMMISSION_PERIODS[g.commission_period || "quarterly"]}</div>
                    ) : null}
                  </td>
                  <td className={`px-4 py-3 text-right font-mono-num font-semibold ${g.status === "called" ? "text-rose-600" : ""}`}>
                    {fmtTRY(g.amount)}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {g.status !== "cancelled" && (
                      <button
                        data-testid={`guarantee-status-btn-${g.id}`}
                        onClick={() => setStatusModal(g)}
                        className="p-1.5 hover:bg-neutral-100" title="Durum Değiştir"
                      >
                        <ArrowLeftRight size={14} />
                      </button>
                    )}
                    <button onClick={() => { setEditing(g); setOpen(true); }} className="p-1.5 hover:bg-neutral-100">
                      <Edit3 size={14} />
                    </button>
                    <button data-testid={`guarantee-delete-${g.id}`} onClick={() => remove(g.id)} className="p-1.5 hover:bg-rose-50">
                      <Trash2 size={14} className="text-rose-600" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {open && (
        <GuaranteeModal
          editing={editing} contacts={contacts} accounts={accounts}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
      {statusModal && (
        <StatusModal
          guarantee={statusModal} accounts={accounts}
          onClose={() => setStatusModal(null)}
          onSaved={() => { setStatusModal(null); load(); }}
        />
      )}

      {importOpen && (
        <GuaranteeImportModal
          onClose={() => setImportOpen(false)}
          onImported={() => load()}
        />
      )}
    </div>
  );
}

function SortableTh({ children, field, sort, onClick, align = "left" }) {
  const active = sort.field === field;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  const justify = align === "right" ? "justify-end" : "justify-start";
  return (
    <th className={`${align === "right" ? "text-right" : "text-left"} px-4 py-3 label-caps`}>
      <button
        type="button"
        onClick={() => onClick(field)}
        data-testid={`guarantee-sort-${field}`}
        className={`flex items-center gap-1 ${justify} w-full hover:text-black ${active ? "text-black" : "text-neutral-600"}`}
      >
        {children}
        <Icon size={12} className={active ? "opacity-100" : "opacity-40"} />
      </button>
    </th>
  );
}


function StatCard({ label, value, tone, icon: Icon, subtitle, tid }) {
  const color = tone === "rose" ? "text-rose-600" : tone === "amber" ? "text-amber-600" : tone === "emerald" ? "text-emerald-700" : "";
  return (
    <div className="brutal-card p-5" data-testid={tid}>
      <div className="flex items-start justify-between">
        <span className="label-caps">{label}</span>
        <Icon size={16} className="text-neutral-500" />
      </div>
      <div className={`font-mono-num text-2xl md:text-3xl font-semibold mt-3 ${color}`}>
        {fmtTRY(value)}
      </div>
      {subtitle && <div className="text-[11px] text-neutral-500 mt-1">{subtitle}</div>}
    </div>
  );
}

function GuaranteeModal({ editing, contacts, accounts, onClose, onSaved }) {
  const [form, setForm] = useState(editing || {
    letter_number: "",
    bank_name: "",
    letter_type: "definitive",
    amount: "",
    currency: "TRY",
    issue_date: todayISO(),
    expiry_date: todayISO(),
    beneficiary_name: "",
    beneficiary_contact_id: "",
    project: "",
    commission_rate: "",
    commission_period: "quarterly",
    account_id: "",
    notes: "",
  });

  const save = async (e) => {
    e.preventDefault();
    const p = { ...form,
      amount: Number(form.amount),
      commission_rate: form.commission_rate === "" ? null : Number(form.commission_rate),
    };
    try {
      if (editing) await api.put(`/guarantees/${editing.id}`, p);
      else await api.post("/guarantees", p);
      toast.success("Kaydedildi"); onSaved();
    } catch { toast.error("Kaydedilemedi"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={save}
        data-testid="guarantee-form-modal"
        className="bg-white border border-black w-full max-w-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <h2 className="font-display text-xl font-bold">
            {editing ? "Teminat Mektubunu Düzenle" : "Yeni Teminat Mektubu"}
          </h2>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-4">
          <F label="Mektup Türü">
            <div className="grid grid-cols-3 md:grid-cols-5 gap-1.5">
              {Object.entries(LETTER_TYPES).map(([k, l]) => (
                <button
                  key={k} type="button"
                  data-testid={`guarantee-type-${k}`}
                  onClick={() => setForm({ ...form, letter_type: k })}
                  className={`text-xs uppercase tracking-widest py-2 border ${
                    form.letter_type === k ? "bg-black text-white border-black" : "border-black/15 hover:border-black/40"
                  }`}
                >
                  {l}
                </button>
              ))}
            </div>
          </F>

          <div className="grid grid-cols-2 gap-3">
            <F label="Mektup No">
              <input required data-testid="guarantee-number" value={form.letter_number}
                onChange={(e) => setForm({ ...form, letter_number: e.target.value })}
                className="input font-mono-num" placeholder="TMB-2026-001" />
            </F>
            <F label="Tutar (₺)">
              <input required type="number" step="0.01" data-testid="guarantee-amount"
                value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
                className="input font-mono-num" />
            </F>
          </div>

          <F label="Banka">
            <input required data-testid="guarantee-bank" value={form.bank_name}
              onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
              className="input" placeholder="Ziraat Bankası" />
          </F>

          <div className="grid grid-cols-2 gap-3">
            <F label="Düzenleme Tarihi">
              <input required type="date" data-testid="guarantee-issue-date"
                value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
                className="input" />
            </F>
            <F label="Vade Tarihi">
              <input required type="date" data-testid="guarantee-expiry-date"
                value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })}
                className="input" />
            </F>
          </div>

          <F label="Lehtar (Alıcı Kurum)">
            <input required data-testid="guarantee-beneficiary" value={form.beneficiary_name}
              onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })}
              className="input" placeholder="Örn: X A.Ş., Devlet Kurumu vs." />
          </F>

          <F label="Lehtar Cari (Opsiyonel)">
            <select data-testid="guarantee-contact" value={form.beneficiary_contact_id || ""}
              onChange={(e) => setForm({ ...form, beneficiary_contact_id: e.target.value })}
              className="input bg-white">
              <option value="">—</option>
              {contacts.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </F>

          <F label="Proje / Sözleşme (Opsiyonel)">
            <input data-testid="guarantee-project" value={form.project || ""}
              onChange={(e) => setForm({ ...form, project: e.target.value })}
              className="input" placeholder="Örn: Ankara Metro İhalesi" />
          </F>

          <div className="grid grid-cols-2 gap-3">
            <F label="Komisyon Oranı % (Yıllık)">
              <input type="number" step="0.01" data-testid="guarantee-commission"
                value={form.commission_rate || ""} onChange={(e) => setForm({ ...form, commission_rate: e.target.value })}
                className="input font-mono-num" placeholder="1.5" />
            </F>
            <F label="Komisyon Dönemi">
              <select data-testid="guarantee-period" value={form.commission_period || "quarterly"}
                onChange={(e) => setForm({ ...form, commission_period: e.target.value })}
                className="input bg-white">
                {Object.entries(COMMISSION_PERIODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </F>
          </div>

          <F label="Notlar">
            <textarea rows={2} value={form.notes || ""}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="input" />
          </F>
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button type="button" onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="guarantee-save-button" type="submit" className="brutal-btn bg-black text-white px-5 py-2 text-sm">
            Kaydet
          </button>
        </div>
        <style>{`.input{width:100%;border:1px solid rgba(0,0,0,0.2);padding:0.5rem 0.75rem;outline:none}`}</style>
      </form>
    </div>
  );
}

function StatusModal({ guarantee, accounts, onClose, onSaved }) {
  const [newStatus, setNewStatus] = useState("");
  const [accountId, setAccountId] = useState(guarantee.account_id || "");
  const [dateVal, setDateVal] = useState(todayISO());

  const opts = [
    { key: "returned", label: "İade Edildi (Serbest Bırakıldı)", icon: ArchiveRestore },
    { key: "called", label: "Nakde Çevrildi (Hakediş)", icon: ShieldAlert },
    { key: "expired", label: "Süresi Doldu", icon: AlertTriangle },
    { key: "cancelled", label: "İptal Et", icon: Ban },
    { key: "active", label: guarantee.status === "called" ? "Geri Aktif Et (Gider Silinir)" : "Aktif Et", icon: ShieldCheck },
  ].filter((o) => o.key !== guarantee.status);

  const apply = async () => {
    if (!newStatus) return;
    try {
      const payload = { status: newStatus };
      if (newStatus === "called") {
        if (!accountId) { toast.error("Nakde çevrilecek hesap seçin"); return; }
        payload.account_id = accountId;
        payload.called_date = dateVal;
      }
      if (newStatus === "returned") payload.returned_date = dateVal;
      await api.post(`/guarantees/${guarantee.id}/status`, payload);
      toast.success("Durum güncellendi");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Hata");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="guarantee-status-modal"
           className="bg-white border border-black w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b-2 border-black">
          <div>
            <div className="label-caps">TM Durumu</div>
            <h2 className="font-display text-lg font-bold">{guarantee.letter_number} · {fmtTRY(guarantee.amount)}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-5 space-y-2">
          {opts.map((o) => (
            <button key={o.key} data-testid={`guarantee-set-status-${o.key}`}
              onClick={() => setNewStatus(o.key)}
              className={`w-full text-left border px-4 py-3 flex items-center gap-3 transition-colors ${
                newStatus === o.key ? "border-black bg-neutral-100" : "border-black/15 hover:border-black/40"
              }`}>
              <o.icon size={16} />
              <span className="text-sm font-medium">{o.label}</span>
            </button>
          ))}

          {(newStatus === "called" || newStatus === "returned") && (
            <div className="pt-2">
              <div className="label-caps mb-1.5">
                {newStatus === "called" ? "Nakde Çevirme Tarihi" : "İade Tarihi"}
              </div>
              <input type="date" data-testid="guarantee-status-date"
                value={dateVal} onChange={(e) => setDateVal(e.target.value)}
                className="w-full border border-black/20 px-3 py-2 outline-none" />
            </div>
          )}
          {newStatus === "called" && (
            <div>
              <div className="label-caps mb-1.5">Hangi hesaptan gider oluşturulsun?</div>
              <select data-testid="guarantee-called-account" value={accountId}
                onChange={(e) => setAccountId(e.target.value)}
                className="w-full border border-black/20 px-3 py-2 bg-white outline-none">
                <option value="">— Seçin —</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
              <div className="text-[11px] text-neutral-500 mt-1.5 border-l-2 border-rose-500 pl-3">
                Nakde çevrildiğinde bu hesaptan mektup tutarında otomatik gider oluşturulur.
              </div>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-black/10 bg-neutral-50">
          <button onClick={onClose} className="border border-black/20 px-4 py-2 text-sm">Vazgeç</button>
          <button data-testid="guarantee-status-apply" onClick={apply} disabled={!newStatus}
                  className="brutal-btn bg-black text-white px-5 py-2 text-sm disabled:opacity-40">
            Uygula
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <label className="block"><div className="label-caps mb-1.5">{label}</div>{children}</label>;
}
